import { observer } from "mobx-react";
import React, { useState, useEffect } from "react";
import { Dialog, Button, Text, Group } from "@mantine/core";
import { BobsWorkspace } from "../stores/BobsWorkspace";
import * as Sentry from "@sentry/react";

// Convert base64 to base64url (remove padding and replace characters)
const base64ToBase64url = (base64: string): string => {
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
};

const subscribe = async (vapidPublicKey: string) => {
  console.log("subscribe function called with VAPID key:", vapidPublicKey);
  const registration = await navigator.serviceWorker.ready;
  console.log("Service worker registration ready:", registration);

  let subscription = await registration.pushManager.getSubscription();
  console.log("Existing subscription:", subscription);

  if (!subscription) {
    console.log("No existing subscription, creating new one...");
    // Convert the VAPID key to base64url format
    const base64urlKey = base64ToBase64url(vapidPublicKey);
    console.log("Converted VAPID key:", base64urlKey);

    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64urlKey,
    });
    console.log("New subscription created:", subscription);

    if (!subscription) {
      console.error("Web push subscription failed");
    }
  } else {
    console.log("Using existing subscription");
  }

  return subscription;
};

// Export function to unsubscribe from push notifications
export const unsubscribeFromPushNotifications = async () => {
  try {
    if ("serviceWorker" in navigator && "PushManager" in window) {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        console.log("Unsubscribing from push notifications...");
        await subscription.unsubscribe();
        console.log("Successfully unsubscribed from push notifications");
        return true;
      } else {
        console.log("No active subscription found");
        return false;
      }
    }
  } catch (error) {
    console.error("Error unsubscribing from push notifications:", error);
    Sentry.captureException(error, {
      tags: {
        function: "unsubscribeFromPushNotifications",
        action: "unsubscribe",
      },
      contexts: {
        notification_details: {
          hasServiceWorker: "serviceWorker" in navigator,
          hasNotifications: "Notification" in window,
          notificationPermission: Notification.permission,
          errorMessage: error?.message || "Unknown error",
        },
      },
    });
    return false;
  }
};

const BrowserNotifications = observer(
  ({ bobsWorkspace }: { bobsWorkspace: BobsWorkspace }) => {
    const [dialogOpened, setDialogOpened] = useState(false);
    const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const selectedBob = bobsWorkspace.selectedBob;
    // console.log(
    //   "number of known browser subscriptions",
    //   bobsWorkspace.accountNumberOfServerKnownBrowserSubscriptions
    // );

    // Check if this device already has an active push subscription
    useEffect(() => {
      const checkSubscription = async () => {
        try {
          if ("serviceWorker" in navigator && "PushManager" in window) {
            const registration = await navigator.serviceWorker.ready;
            const subscription =
              await registration.pushManager.getSubscription();

            const browserHasSubscription = !!subscription;
            const serverKnowsSubscriptions =
              bobsWorkspace.accountNumberOfServerKnownBrowserSubscriptions > 0;

            // If browser has subscription but server has 0 known subscriptions,
            // this is an orphaned subscription that should be cleaned up
            if (browserHasSubscription && !serverKnowsSubscriptions) {
              console.log(
                "Found orphaned browser subscription, cleaning up..."
              );
              await subscription.unsubscribe();
              console.log("Orphaned subscription cleaned up");
              setHasActiveSubscription(false);
            } else {
              setHasActiveSubscription(browserHasSubscription);
            }
          }
        } catch (error) {
          console.error("Error checking subscription:", error);
          Sentry.captureException(error, {
            tags: {
              function: "checkActiveSubscription",
              action: "subscription_check",
            },
            contexts: {
              bob_details: {
                bobId: selectedBob?.uuid,
                hasServiceWorker: "serviceWorker" in navigator,
                hasNotifications: "Notification" in window,
                notificationPermission: Notification.permission,
                errorMessage: error?.message || "Unknown error",
              },
            },
          });
        } finally {
          setIsInitialized(true);
        }
      };

      checkSubscription();
    }, [bobsWorkspace.accountNumberOfServerKnownBrowserSubscriptions]);

    // Set dialog opened state after initialization
    useEffect(() => {
      if (isInitialized) {
        const shouldShow =
          bobsWorkspace.accountUserWantsBrowserNotifications !== false &&
          (typeof Notification === "undefined" ||
            Notification.permission !== "denied") &&
          !hasActiveSubscription;

        setDialogOpened(shouldShow);
      }
    }, [
      isInitialized,
      bobsWorkspace.accountUserWantsBrowserNotifications,
      hasActiveSubscription,
    ]);

    const handleNo = () => {
      if (selectedBob) {
        selectedBob.sendDataAndDirectiveToRails(
          "set-user-wants-browser-notifications",
          {
            userWantsBrowserNotifications: false,
          }
        );
      }
      setDialogOpened(false);
    };

    const handleYes = async () => {
      try {
        console.log("in handleYes");

        // Check if Notification API is available
        if (typeof Notification === "undefined") {
          console.error("Notification API not available");
          setDialogOpened(false);
          return;
        }

        console.log(
          "Current notification permission:",
          Notification.permission
        );

        let permission = Notification.permission;

        // Only request permission if it's not already granted
        if (permission === "default") {
          console.log("Requesting notification permission...");

          try {
            // Reduce timeout for better UX and handle Edge throttling
            const permissionPromise = Notification.requestPermission();
            const timeoutPromise = new Promise<NotificationPermission>(
              (_, reject) =>
                setTimeout(
                  () => reject(new Error("Permission request timeout")),
                  10000 //
                )
            );

            permission = await Promise.race([
              permissionPromise,
              timeoutPromise,
            ]);
          } catch (permissionError) {
            console.log(
              "Permission request failed or timed out:",
              permissionError
            );
            Sentry.captureException(permissionError, {
              tags: {
                function: "enableNotifications",
                action: "permission_request",
              },
              contexts: {
                notification_details: {
                  hasServiceWorker: "serviceWorker" in navigator,
                  hasNotifications: "Notification" in window,
                  initialPermission: Notification.permission,
                  userAgent: navigator.userAgent,
                  errorMessage: permissionError?.message || "Unknown error",
                },
              },
            });

            // For Edge compatibility: if permission request fails,
            // still try to proceed if user might have notifications enabled
            // but the API is being throttled
            console.log("Attempting to proceed despite permission timeout...");

            // Check current permission state again after timeout
            permission = Notification.permission;

            if (permission === "default") {
              // Show user-friendly message about permission issue
              alert(
                "Notification permission request timed out. Your browser is blocking notifications. Please try again later or a try a different browser."
              );
              setDialogOpened(false);
              return;
            }
          }
        }

        console.log(`Final notification permission: ${permission}`);

        if (permission === "granted") {
          console.log("Permission granted, calling subscribe...");
          // Get push subscription
          const subscription = await subscribe(bobsWorkspace.vapidPublicKey);
          console.log("Push subscription:", subscription?.toJSON());

          if (selectedBob && subscription) {
            console.log("Sending subscription data to Rails...");
            const subscriptionData = subscription.toJSON();
            selectedBob.sendDataAndDirectiveToRails(
              "set-user-wants-browser-notifications",
              {
                userWantsBrowserNotifications: true,
                subscriptionData: subscriptionData,
              }
            );
            console.log("Data sent to Rails successfully");
          } else {
            console.log("Missing selectedBob or subscription:", {
              selectedBob: !!selectedBob,
              subscription: !!subscription,
            });
          }
        } else {
          console.log("Permission not granted:", permission);
        }
      } catch (error) {
        console.error("Error requesting notification permission:", error);
        Sentry.captureException(error, {
          tags: {
            function: "enableNotifications",
            action: "subscription_process",
          },
          contexts: {
            bob_details: {
              bobId: selectedBob?.uuid,
              hasServiceWorker: "serviceWorker" in navigator,
              hasNotifications: "Notification" in window,
              notificationPermission: Notification.permission,
              vapidPublicKey: bobsWorkspace?.vapidPublicKey
                ? "provided"
                : "missing",
              errorMessage: error?.message || "Unknown error",
            },
          },
        });
      }
      setDialogOpened(false);
    };

    // Show dialog logic:
    // 1. If user explicitly said NO (false) - don't show
    // 2. If browser notifications are denied - don't show
    // 3. If this device already has an active subscription - don't show
    // 4. Otherwise show (covers: null, true but no subscription on this device)
    if (!isInitialized || !dialogOpened) {
      return null;
    }

    return (
      <Dialog
        opened={dialogOpened}
        onClose={() => setDialogOpened(false)}
        position={{ top: 20, left: 20 }}
        size="lg"
        radius="md"
      >
        <Text size="sm" mb="xs" fw={500}>
          {bobsWorkspace.accountUserWantsBrowserNotifications === true
            ? "Set up notifications on this device?"
            : "Do you want browser notifications for payments?"}
        </Text>

        <Group>
          <Button onClick={handleYes} color="blue">
            Yes
          </Button>
          <Button onClick={handleNo} variant="outline">
            No
          </Button>
        </Group>
      </Dialog>
    );
  }
);

export default BrowserNotifications;
